<template>
  <div>
    <div v-if="isResumingLearningPath || isResumingCourse">
      <ResumeFromLearningPath
        v-if="isResumingLearningPath"
        v-bind="{
          learningPath,
          course,
          learningArea,
          instructor,
          courseLinkProperties,
          hasNextUpContent,
          nextUpProperties,
          isLoading,
        }"
        @resume-content="handleResume"
      />
      <ResumeFromCourse
        v-else
        v-bind="{
          course,
          learningArea,
          instructor,
          courseLinkProperties,
          hasNextUpContent,
          nextUpProperties,
          isLoading,
        }"
        @resume-content="handleResume"
      />
    </div>
    <div v-else>
      <div
        :style="`background-image: url(${$getPlaylistBackground(
          fallbackLearningPath.id
        )}), linear-gradient(#151515, #262626); background-size: cover;`"
      >
        <Fallback />
        <FallbackCourseListing />
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import CONSTANTS from '@/constants'
import ResumeFromLearningPath from './resume-from-recent-activity/ResumeFromLearningPath.vue'
import ResumeFromCourse from './resume-from-recent-activity/ResumeFromCourse.vue'
import Fallback from './resume-from-recent-activity/Fallback.vue'
import FallbackCourseListing from './resume-from-recent-activity/FallbackCourseListing.vue'

export default {
  name: 'ResumeFromRecentActivity',
  components: {
    ResumeFromLearningPath,
    ResumeFromCourse,
    Fallback,
    FallbackCourseListing,
  },
  props: {
    learningPath: {
      type: Object,
      required: false,
    },
    course: {
      type: Object,
      required: false,
    },
    learningArea: {
      type: String,
      required: true,
    },
    instructor: {
      type: String,
      required: true,
    },
    nextUpTypeAndId: {
      type: Object,
      required: false,
    },
    nextUpContent: {
      type: Object,
      required: true,
    },
    learningPathBackground: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
    }
  },
  computed: {
    isResumingLearningPath() {
      return !!this.learningPath?.id
    },
    isResumingCourse() {
      return !!this.course?.id
    },
    hasNextUpContent() {
      return !!this.nextUpTypeAndId?.id
    },
    nextUpProperties() {
      if (this.nextUpContent && this.nextUpContent.id) {
        return {
          name: this.getPropertyName(this.nextUpContent),
          type: this.getPropertyType(this.nextUpContent),
          id: this.nextUpContent.id,
        }
      }
      return { name: 'INE Content', type: 'video', id: '' }
    },
    courseLinkProperties() {
      return {
        id: this?.course?.id ?? '',
        learning_area: [{ name: this?.learningArea ?? '' }],
        slug: this?.course?.slug ?? '',
      }
    },
    fallbackLearningPath() {
      return CONSTANTS.HOME_PAGE.FALLBACK_CONTENT.LEARNING_PATH
    },
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    getPropertyName(content) {
      const type = this.getPropertyType(content)
      if (type === 'quiz') {
        const { name, metadata } = this.nextUpContent
        return metadata?.quiz_title ? metadata.quiz_title : name
      }
      const { name } = content
      return name && name.length ? name : 'Content'
    },
    getPropertyType(content) {
      let type = 'INE Content'
      if (content?.type) {
        type = content.type
      }
      if (content?.content_type) {
        type = content.content_type
      }
      if (type === 'iframe') {
        return 'slides'
      }
      if (type === 'lesson') {
        return 'video'
      }
      return type
    },
    handleResume() {
      this.$router.push(
        this.$buildContentLink('course', this.courseLinkProperties)
      )
      const { content_type: type, id } = this.nextUpTypeAndId

      switch (type) {
        case 'video':
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setPlayingVideoId', id)
          break
        case 'lesson':
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setPlayingVideoId', id)
          break
        case 'quiz':
          this.$store.dispatch('content/setCurrentQuizId', id)
          break
        case 'slides':
          this.$store.dispatch('iframes/setPlayingIFrameId', id)
          break
        case 'iframe':
          this.$store.dispatch('iframes/setPlayingIFrameId', id)
          break
        case 'lab':
          this.$store.dispatch('labs/setFocusLabId', id)
          break
        case 'exercise':
          this.$store.dispatch('exercises/setResumeExerciseId', id)
          break
        default:
          break
      }
    },
  },
  async created() {
    this.isLoading = true
    if (this.nextUpTypeAndId && this.nextUpTypeAndId.id) {
      if (this.course?.id) {
        this.setParentContent({ ...this.course, type: 'course' })
        await this.$store.dispatch(
          'content/getRecentActivityNextUpContent',
          this.nextUpTypeAndId
        )
      }
    }
    this.isLoading = false
  },
}
</script>

<style lang="scss">
.lphero {
  position: relative;
  width: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  z-index: 1;
  margin: 0 0 35px 0;
  @include tablet {
    margin: 0 0 10px 0;
  }
  @include tablet {
    min-height: 55vh;
  }
  &__overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: -1;
    background: linear-gradient(
      to top,
      rgba(21, 21, 21, 0),
      rgba(21, 21, 21, 0),
      rgba(21, 21, 21, 0.3),
      rgba(21, 21, 21, 0.7),
      rgba(21, 21, 21, 1)
    );
    @include mobile {
      background: linear-gradient(
        to bottom,
        rgba(21, 21, 21, 0),
        rgba(21, 21, 21, 0),
        rgba(21, 21, 21, 0.3),
        rgba(21, 21, 21, 0.7),
        rgba(21, 21, 21, 1)
      );
    }
  }
  &__content {
    padding: 123px 25px 5px 25px;
    @include mobile {
      padding: 123px 25px 25px 25px;
    }
    @include tablet {
      padding: 220px 100px 130px 100px;
    }
  }
  &__title {
    font-family: var(--font-book);
    font-size: rem(16);
    line-height: 23px;
    color: var(--white);
    padding: 0;
    margin: 0 0 10px 0;
    @include mobile {
      font-size: rem(16);
    }
    @include tablet {
      font-size: rem(18);
      line-height: 24px;
      margin: 0;
    }
  }
  &__name {
    font-family: var(--font-bold);
    font-size: rem(32);
    line-height: 38px;
    color: var(--white);
    max-width: 760px;
    padding: 0;
    margin: 0;
    line-height: 1.2;
    @include mobile {
      font-size: rem(32);
      line-height: 38px;
    }
    @include tablet {
      font-size: rem(40);
      line-height: 52px;
    }
  }
  &__actions {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin: 30px 0 0 0;
    @include tablet {
      flex-wrap: nowrap;
    }
  }
  &__cta-wrapper {
    display: flex;
    flex-direction: column;
    @include mobile {
      flex-direction: row;
    }
  }
  &__separator {
    color: var(--white);
    margin: 0 9px;
    @include mobile {
      margin: 0 12px;
    }
  }
  &__explore-lp {
    margin-top: 20px;
    @include mobile {
      margin: 0 0 0 20px;
    }
  }
  &__progress {
    display: flex;
    margin-bottom: 50px;
  }
  &__progress-bar {
    margin: 8px 0 0 0;
  }
  &__progress-copy {
    &-wrapper {
      font-size: rem(14);
      line-height: 20px;
    }
    font-family: var(--font-book);
    display: inline;
    flex-wrap: wrap;
    padding: 0;
    color: var(--white);
    position: relative;
    &--percentage {
      font-size: inherit;
      display: inline;
      color: var(--positive-light);
    }
  }
  &__link {
    width: 100%;
    @include tablet {
      width: fit-content;
    }
  }
  &__nextup-loader {
    position: absolute;
    right: -40px;
    top: -15px;
  }
  &__nextup {
    text-transform: capitalize;
    margin: 0 0 0 3px;
  }
}
</style>
